/**
 * Property details page showing all property information.
 * For investors, also shows their offer status on this property.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { useDataView } from '@/core/data'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { AlertDialog } from '@/common/components/alert-dialog'
import { Stack } from '@/common/components/stack'

import { getProperty, deleteProperty, updateProperty, reassignProperty, PropertySelect, propertyDetailQueryKey } from '@/features/properties/api/properties'
import type { PropertyResponse, PropertyStatus } from '@/features/properties/api/properties'
import { PropertyImageGallery } from '@/features/properties/components/PropertyImageGallery'
import { PropertyConditionSection } from '@/features/properties/components/PropertyConditionSection'
import { PropertyPricingGrid } from '@/features/properties/components/PropertyPricingGrid'
import { PropertyStatsGrid } from '@/features/properties/components/PropertyStatsGrid'
import { AdditionalNotesSection } from '@/features/properties/components/AdditionalNotesSection'
import { PropertyStatusBadge } from '@/features/properties/components/PropertyStatusBadge'
import { PropertySidePanel, YOUR_OFFER_ANCHOR_ID } from '@/features/properties/components/PropertySidePanel'
import { ReassignAgentDialog } from '@/features/properties/components/ReassignAgentDialog'
import { DeliverToPartnerDialog } from '@/features/partners/components/DeliverToPartnerDialog'
import { formatSubmittedAgo } from '@/features/properties/utils/submittedAgo'

import { useOffersSource, type OfferWithPropertyResponse } from '@/features/offers'

import * as styles from './index.css'

export const Route = createFileRoute({
  // focus=offer (from /properties/$id/counter) scrolls the investor to their offer card
  validateSearch: (search: Record<string, unknown>): { focus?: 'offer' } =>
    search.focus === 'offer' ? { focus: 'offer' } : {},
  component: PropertyDetailsPage,
})

// =============================================================================
// Property Info Section (below gallery, in main column)
// =============================================================================

interface PropertyInfoSectionProps {
  property: PropertyResponse
  isAgent?: boolean
  showDhWarnings?: boolean
}

function PropertyInfoSection({ property, isAgent, showDhWarnings }: PropertyInfoSectionProps) {
  const { id, address, status, submitted_at } = property
  const dh = property.deal_hub_data

  return (
    <div className={styles.propertyInfo}>
      {/* Address and Status Row */}
      <div className={styles.addressRow}>
        <div className={styles.addressStack}>
          <Text as="h1" variant="h4" weight="bold" className={styles.addressText}>
            {address.street_address}
            {address.unit_number && ` ${address.unit_number}`}
          </Text>
          <Text variant="body" className={styles.cityStateZip}>
            {address.city}, {address.state} {address.zipcode}
          </Text>
          {submitted_at && (
            <Text variant="bodySm" color="muted" className={styles.submittedDate}>
              {formatSubmittedAgo(submitted_at)}
            </Text>
          )}
        </div>
        <PropertyStatusBadge
          propertyId={id}
          status={status as PropertyStatus}
          hasTerminalOutcome={property.has_terminal_outcome ?? false}
          openUnderContractOutcome={property.open_under_contract_outcome ?? null}
          propertyAddress={address.street_address}
          propertyClient={property.client ?? undefined}
        />
      </div>

      <PropertyPricingGrid
        property={property}
        sharing={
          isAgent
            ? { market_value: property.share_market_value ?? false, floor_price: property.share_floor_price ?? false }
            : undefined
        }
        className={styles.infoGrid}
      />

      <PropertyStatsGrid
        property={property}
        dealHub={showDhWarnings ? dh : undefined}
        className={styles.infoGrid}
      />

      {/* Additional DH mismatch warnings for fields not in the stats grid */}
      {showDhWarnings && (
        <div className={styles.dhMismatchList}>
          {property.property_type && dh?.dh_property_type != null && property.property_type !== dh.dh_property_type && (
            <Text variant="bodyXs" className={styles.dhWarning}>
              Property type: {property.property_type} — Deal Hub: {dh.dh_property_type}
            </Text>
          )}
          {property.client && dh?.dh_client != null && property.client !== dh.dh_client && (
            <Text variant="bodyXs" className={styles.dhWarning}>
              Client: {property.client} — Deal Hub: {dh.dh_client}
            </Text>
          )}
        </div>
      )}
    </div>
  )
}

function PropertyDetailsPage() {
  // Try to get id from params, fall back to extracting from URL
  // This fallback handles a TanStack Router issue where params may be empty on client-side navigation
  const params = useParams({ strict: false }) as { id?: string }
  let id = params.id

  if (!id && typeof window !== 'undefined') {
    const match = window.location.pathname.match(/\/properties\/([^/]+)/)
    id = match?.[1]
  }

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { token, roles } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()

  const isInvestor = roles?.includes('investor')
  const isAgent = roles?.includes('agent')
  const isAdmin = roles?.includes('admin')

  // The outcome fields are agent-only, and naming an inaccessible field in
  // `select` 403s the whole request — so only ask for them when the viewer can
  // actually change status, which is the only thing that reads them.
  const canChangeStatus = Boolean(isAgent || isAdmin)

  // Delete state (for draft properties)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Submit to investors state (for draft properties)
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reassign agent state (admin-only)
  const [showReassignDialog, setShowReassignDialog] = useState(false)

  // Deliver to partner state (admin-only): send this property to a partner now.
  const [showDeliverDialog, setShowDeliverDialog] = useState(false)

  // Fetch property data
  const {
    data: property,
    isLoading: propertyLoading,
    error: propertyError,
  } = useQuery({
    queryKey: propertyDetailQueryKey(id ?? '', canChangeStatus),
    queryFn: () =>
      getProperty(token!, id!, canChangeStatus ? { select: PropertySelect.detailWithOutcome } : undefined),
    enabled: !!token && !!id,
  })

  // Fetch offers on this property
  // - For investors: to show their offer status
  // - For agents/admins: to show "See all offers" button if there are any
  const offersSource = useOffersSource()
  const offersView = useDataView({
    source: offersSource,
    initialParams: {
      filters: { propertyIds: id ? [id] : [] },
    },
    enabled: !!id,
  })

  const hasOffers = (offersView.totalCount ?? 0) > 0

  // Get the user's offer on this property (if any)
  const userOffer: OfferWithPropertyResponse | undefined = useMemo(() => {
    if (!isInvestor || !offersView.data || offersView.data.length === 0) {
      return undefined
    }
    // An open offer first: the API allows only one, and it is the one the investor can act on
    return offersView.data.find((o) => o.status === 'pending' || o.status === 'engaged') ?? offersView.data[0]
  }, [isInvestor, offersView.data])

  const { focus } = useSearch({ strict: false }) as { focus?: string }
  const focusOffer = focus === 'offer'
  const scrolledToOffer = useRef(false)
  useEffect(() => {
    if (!focusOffer || scrolledToOffer.current) return
    // The card renders only once both the property and the offer have loaded.
    const card = document.getElementById(YOUR_OFFER_ANCHOR_ID)
    if (!card) return
    scrolledToOffer.current = true
    card.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusOffer, userOffer, property])

  // Delete handler for draft properties
  const handleDelete = async () => {
    if (!token || !id) return

    setIsDeleting(true)
    try {
      await deleteProperty(token, id)
      // Invalidate DataSource queries (used by DataTable/useDataView)
      queryClient.invalidateQueries({ queryKey: ['dataSource', 'properties'] })
      // Invalidate direct property queries
      queryClient.invalidateQueries({ queryKey: ['property', id] })
      showSuccessToast('Draft property deleted successfully')
      navigate({ to: '/properties' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete property'
      showErrorToast(message)
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  // Submit to investors handler (changes status from draft to available)
  const handleSubmitToInvestors = async () => {
    if (!token || !id || !property) return

    setIsSubmitting(true)
    try {
      await updateProperty(token, id, { status: 'available' })
      // Invalidate queries to refresh the property data
      queryClient.invalidateQueries({ queryKey: ['dataSource', 'properties'] })
      queryClient.invalidateQueries({ queryKey: ['property', id] })
      showSuccessToast('Property submitted to investors successfully')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit property'
      showErrorToast(message)
    } finally {
      setIsSubmitting(false)
      setShowSubmitDialog(false)
    }
  }

  // Reassign agent handler (admin-only). Errors are re-thrown so the dialog can
  // surface them inline and stay open on failure.
  const handleReassignAgent = async (newOwnerId: string) => {
    if (!token || !id) return

    try {
      await reassignProperty(token, id, newOwnerId)
      queryClient.invalidateQueries({ queryKey: ['dataSource', 'properties'] })
      queryClient.invalidateQueries({ queryKey: ['property', id] })
      showSuccessToast('Property reassigned successfully')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reassign property'
      showErrorToast(message)
      throw err
    }
  }

  const isDraft = property?.status === 'draft'

  // Loading state - show spinner if query is loading OR if token isn't ready yet
  if (propertyLoading || !token) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state - only show if query actually ran and failed
  if (propertyError || !property) {
    return (
      <div className={styles.container}>
        <Link to="/properties" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back to Properties
        </Link>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="h4" className={styles.errorTitle}>Property Not Found</Text>
          <Text variant="body" color="muted" className={styles.errorMessage}>
            The property you're looking for doesn't exist or you don't have
            permission to view it.
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Header with back link and edit button */}
      <div className={styles.pageHeader}>
        <Link to="/properties" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back to properties
        </Link>
        {(isAgent || isAdmin) && (
          <Stack direction="row" gap="8" align="center" className={styles.headerActions}>
            {isDraft ? (
              /* Draft: Delete, Edit, Submit to Investors buttons */
              (<>
                <Button
                  variant="outlineDanger"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Icon name="delete" size={16} />
                  Delete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate({ to: '/properties/$id/edit', params: { id: property.id } })}
                >
                  <Icon name="edit" size={16} />
                  Edit
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowSubmitDialog(true)}
                >
                  <Icon name="send" size={16} />
                  <span className={styles.submitTextFull}>Submit to Investors</span>
                  <span className={styles.submitTextShort}>Submit</span>
                </Button>
              </>)
            ) : (
              /* Non-draft: Deliver to partner (admin), View offers (if any), Edit */
              (<>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeliverDialog(true)}
                  >
                    <Icon name="send" size={16} />
                    Deliver to partner
                  </Button>
                )}
                {hasOffers && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate({ to: '/properties/$id/offers', params: { id: property.id }, search: { offerId: undefined } })}
                  >
                    View offers
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate({ to: '/properties/$id/edit', params: { id: property.id } })}
                >
                  <Icon name="edit" size={16} />
                  Edit
                </Button>
              </>)
            )}
          </Stack>
        )}
      </div>
      {/* Two-column layout */}
      <div className={styles.content}>
        {/* Main column - images, property info, and conditions */}
        <div className={styles.mainColumn}>
          <div className={styles.propertyOverview} data-tour-step="property-info">
            <PropertyImageGallery media={property.media || []} />
            <PropertyInfoSection
              property={property}
              isAgent={isAgent || isAdmin}
              showDhWarnings={
                (isAgent || isAdmin) &&
                !!property.deal_hub_data?.agent_modified
              }
            />
            <PropertyConditionSection property={property} />
          </div>
          <AdditionalNotesSection property={property} />
        </div>

        {/* Side column */}
        <div className={styles.sideColumn}>
          <PropertySidePanel
            property={property}
            isInvestor={isInvestor}
            userOffer={userOffer}
            onOfferWithdraw={offersView.refetch}
            isAgentOrAdmin={isAgent || isAdmin}
            isAdmin={isAdmin}
            onReassignAgent={() => setShowReassignDialog(true)}
          />
        </div>
      </div>
      {/* Delete Draft Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialog.Content>
          <AlertDialog.Title>Delete Draft Property</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to delete this draft property? This action is permanent and cannot be undone.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isDeleting}>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action
              variant="danger"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
      {/* Submit to Investors Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialog.Content>
          <AlertDialog.Title>Submit to Investors</AlertDialog.Title>
          <AlertDialog.Description>
            Are you sure you want to submit this property to investors? Matching investors will be notified and can start submitting offers.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel disabled={isSubmitting}>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action
              onClick={handleSubmitToInvestors}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
      {/* Deliver to partner (admin-only) */}
      {isAdmin && (
        <DeliverToPartnerDialog
          open={showDeliverDialog}
          onOpenChange={setShowDeliverDialog}
          propertyId={property.id}
          propertyLabel={property.address?.street_address ?? 'this property'}
        />
      )}
      {/* Reassign Agent Dialog (admin-only) */}
      {isAdmin && property.owner && (
        <ReassignAgentDialog
          open={showReassignDialog}
          onOpenChange={setShowReassignDialog}
          currentAgentName={`${property.owner.first_name} ${property.owner.last_name}`}
          currentOwnerId={property.owner_id}
          isDealHubLinked={!!property.deal_hub_data}
          onSubmit={handleReassignAgent}
        />
      )}
    </div>
  )
}
